import {
  type ConfirmModalProps,
  notify,
  useConfirmModal,
} from '@affine/component';
import { AuthService, DefaultServerService } from '@affine/core/modules/cloud';
import { UserFriendlyError } from '@affine/error';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService, useServices } from '@toeverything/infra';
import { useCallback } from 'react';

import { useNavigateHelper } from '../use-navigate-helper';

type SignOutConfirmModalI18NKeys =
  | 'title'
  | 'description'
  | 'cancel'
  | 'confirm';

export const useSignOut = ({
  onConfirm,
  confirmButtonOptions,
  contentOptions,
  ...props
}: ConfirmModalProps = {}) => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const { jumpToSignIn, jumpToIndex } = useNavigateHelper();

  const authService = useService(AuthService);
  const { defaultServerService } = useServices({ DefaultServerService });

  const signOut = useCallback(async () => {
    onConfirm?.()?.catch(console.error);
    const enableLocalWorkspace =
      BUILD_CONFIG.isNative ||
      defaultServerService.server.config$.value.features.includes(
        ServerFeature.LocalWorkspace
      );

    try {
      // tuninho: com o login único OBRIGATÓRIO (card 1893242319616869943, fase 3), sair do
      // Mirezi sai também do login único — e por ele das outras ferramentas. A volta é a raiz
      // do Mirezi, que leva de novo ao login único (`plugins/tuninho/login-unico.ts`).
      // A sessão do Mirezi sai por um POST direto, e NÃO pelo `authService.signOut()`: ele
      // revalida a sessão, e o app, vendo-se sem login, navega sozinho para a entrada no mesmo
      // instante — essa navegação cancelava a ida a `/api/tuninho/sair`, e o login único (ainda
      // vivo) relogava a pessoa (medido no dev2, 25/10: o "Sair" não tirava ninguém).
      const tuninhoObrigatorio =
        !BUILD_CONFIG.isNative &&
        (await fetch('/api/tuninho/login-unico', { credentials: 'same-origin' })
          .then(r => (r.ok ? r.json() : null))
          .then(
            (d: { obrigatorio?: boolean } | null) => d?.obrigatorio === true
          )
          .catch(() => false));
      if (tuninhoObrigatorio) {
        const csrf = document.cookie
          .split('; ')
          .find(c => c.startsWith('affine_csrf_token='))
          ?.slice('affine_csrf_token='.length);
        await fetch('/api/auth/sign-out', {
          method: 'POST',
          credentials: 'same-origin',
          headers: csrf
            ? { 'x-affine-csrf-token': decodeURIComponent(csrf) }
            : undefined,
        }).catch(() => undefined);
        window.location.replace('/api/tuninho/sair');
        return;
      }
      await authService.signOut();
      if (enableLocalWorkspace) {
        jumpToIndex();
      } else {
        jumpToSignIn();
      }
    } catch (err) {
      console.error(err);
      const error = UserFriendlyError.fromAny(err);
      notify.error(error);
    }
  }, [authService, jumpToIndex, jumpToSignIn, defaultServerService, onConfirm]);

  const getDefaultText = useCallback(
    (key: SignOutConfirmModalI18NKeys) => {
      return t[`com.affine.auth.sign-out.confirm-modal.${key}`]();
    },
    [t]
  );

  const confirmSignOut = useCallback(() => {
    openConfirmModal({
      title: getDefaultText('title'),
      description: getDefaultText('description'),
      cancelText: getDefaultText('cancel'),
      confirmText: getDefaultText('confirm'),
      confirmButtonOptions: {
        ...confirmButtonOptions,
        variant: 'error',
        ['data-testid' as string]: 'confirm-sign-out-button',
      },
      contentOptions: {
        ...contentOptions,
        ['data-testid' as string]: 'confirm-sign-out-modal',
      },
      onConfirm: signOut,
      ...props,
    });
  }, [
    confirmButtonOptions,
    contentOptions,
    getDefaultText,
    openConfirmModal,
    props,
    signOut,
  ]);

  return confirmSignOut;
};
